import { ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { viewToRoute } from "@/shared/config/routes";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { Button } from "@/v2/components/ui/button";
import { Calendar2Grid } from "./Calendar2Grid";
import {
	type CalendarDaySums,
	DayHoursCell,
	WeekHoursCell,
} from "./Calendar2HourCells";
import { Calendar2YearTimeline } from "./Calendar2YearTimeline";
import { makeEntryPredicate, toYYYYMMDD } from "./Entries/entryFilter";
import { useCanonicalTimekeeperScope } from "./Entries/filters/canonical";
import { isDefaultFilters } from "./Entries/filters/defaults";
import { FilterMenu } from "./Entries/filters/FilterMenu";
import { normalizeOptions } from "./Entries/filters/fields";
import { useFilterOptions } from "./Entries/filters/useFilterOptions";
import { useFilters } from "./Entries/filters/useFilters";
import { StatsBar } from "./Entries/StatsBar";
import { useConfiguredTimekeepers } from "./Entries/timekeepers";
import { useAllEntries } from "./Entries/useAllEntries";

function formatMonthParam(date: Date): string {
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	return `${yyyy}-${mm}`;
}

/**
 * Calendar2 — the in-progress rework of the calendar page. Unlike EntriesView
 * (which scopes to a single day or arbitrary range), this page is month-based:
 * the header navigates whole months and the StatsBar / future calendar grid
 * always reflect the viewed month.
 *
 * It still shares everything else with EntriesView: same entries data
 * (useAllEntries), same filter state, same StatsBar. Filters live in URL query
 * params, so they carry over between entries and calendar automatically.
 *
 * The viewed month is its own URL param (`month`), independent of the date/range
 * selection: paging months here persists across navigation but never changes
 * what Entries scopes to. Any active [from, to] range (or single date) is shown
 * as a highlight only. Selecting a date/range re-anchors the month (see
 * useDateParams). All of this lives in useDateParams, so this view holds no
 * month state of its own.
 *
 * Clicking a day or week cell navigates to Entries scoped to that day / date
 * range, carrying the current query string (filters + everything else) along so
 * the entries view opens with the same filters already applied.
 */
export default function Calendar2View() {
	const { date, dateRange, viewedMonth, setViewedMonth } = useDateParams();
	const navigate = useNavigate();

	// Navigate to /entries for a single day, preserving the current query string
	// (filters, etc). We read window.location.search rather than react-router's
	// useLocation(): nuqs commits filter/date changes straight to the History API,
	// and useLocation() can lag behind those writes — reading the live URL avoids
	// dropping filters that were applied on this page. A single day is from===to
	// (the unified model — see useDateParams); both are omitted when it's today
	// (the default), and the legacy `date` param is dropped.
	const goToEntriesForDay = useCallback(
		(day: Date) => {
			const params = new URLSearchParams(window.location.search);
			const now = new Date();
			const isToday =
				day.getFullYear() === now.getFullYear() &&
				day.getMonth() === now.getMonth() &&
				day.getDate() === now.getDate();
			params.delete("date");
			if (isToday) {
				params.delete("from");
				params.delete("to");
			} else {
				const iso = toYYYYMMDD(day);
				params.set("from", iso);
				params.set("to", iso);
			}
			// A selection re-anchors the calendar — drop the browsed month.
			params.delete("month");
			const search = params.toString();
			navigate(`${viewToRoute.entries}${search ? `?${search}` : ""}`);
		},
		[navigate],
	);

	// Navigate to /entries for a week's [start, end] range, preserving the current
	// query string. The range params win, so the single `date` is cleared.
	const goToEntriesForWeek = useCallback(
		(weekStart: Date, weekEnd: Date) => {
			const params = new URLSearchParams(window.location.search);
			params.set("from", toYYYYMMDD(weekStart));
			params.set("to", toYYYYMMDD(weekEnd));
			params.delete("date");
			// A selection re-anchors the calendar — drop the browsed month.
			params.delete("month");
			navigate(`${viewToRoute.entries}?${params.toString()}`);
		},
		[navigate],
	);

	const { canFetchEntries, requestTimekeeperId: timekeeperRequestScope } =
		useCanonicalTimekeeperScope();
	const { data: entries } = useAllEntries(
		timekeeperRequestScope,
		canFetchEntries,
	);
	const data = entries ?? [];
	const timekeepers = useConfiguredTimekeepers();
	const fieldOptions = useFilterOptions(data, timekeepers);
	const {
		filters,
		defaultFilters,
		toggleFieldValue: toggleFilterFieldValue,
		selectAll: selectAllFilterValues,
		selectNone: selectNoFilterValues,
		reset: resetFilters,
	} = useFilters(fieldOptions);
	const effectiveFilters = filters;
	const visibleEntries = useMemo(() => {
		const predicate = makeEntryPredicate(effectiveFilters, {});
		return data.filter(predicate);
	}, [data, effectiveFilters]);

	// Per-day hour sums, computed from the filtered set MINUS the date window so
	// every day in the grid (including adjacent-month spill days) gets its own
	// totals. Each day carries a grand total plus per-status / per-billability
	// buckets, so the grid can render whichever breakdown the active filter asks
	// for without re-walking the data.
	const sumsByDay = useMemo(() => {
		const map = new Map<string, CalendarDaySums>();
		for (const e of visibleEntries) {
			let agg = map.get(e.date);
			if (!agg) {
				agg = { total: 0, status: new Map(), billable: new Map() };
				map.set(e.date, agg);
			}
			agg.total += e.units;
			agg.status.set(e.status, (agg.status.get(e.status) ?? 0) + e.units);
			agg.billable.set(
				e.billableCategory,
				(agg.billable.get(e.billableCategory) ?? 0) + e.units,
			);
		}
		return map;
	}, [visibleEntries]);

	// The selected status / billability values (operator "is"), or null when no
	// such filter is active. Drives whether — and which — breakdown rows render.
	const statusValues = effectiveFilters.status;
	const billableValues = useMemo(
		() =>
			effectiveFilters.billableCategory ??
			normalizeOptions(fieldOptions.billableCategory ?? []).map(
				(option) => option.value,
			),
		[effectiveFilters.billableCategory, fieldOptions.billableCategory],
	);

	// `viewedMonth` (first-of-month Date) comes straight from useDateParams —
	// backed by the `month` URL param, falling back to the selection's month. The
	// grid always shows whole months; any active [from, to] is a visual highlight
	// only, not a scope change.
	const viewedMonthDate = viewedMonth;

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		// Only fill in a missing month. If a month is already present, even a
		// just-clicked future/past month, never overwrite it from a stale render.
		if (params.has("month")) return;
		params.set("month", formatMonthParam(viewedMonthDate));
		navigate(`${window.location.pathname}?${params.toString()}`, {
			replace: true,
		});
	}, [navigate, viewedMonthDate]);

	// The full [first, last] day span of the viewed month — the window the
	// StatsBar + grid totals filter against.
	const dateFilter = useMemo(() => {
		const year = viewedMonthDate.getFullYear();
		const month = viewedMonthDate.getMonth();
		return {
			from: toYYYYMMDD(new Date(year, month, 1)),
			to: toYYYYMMDD(new Date(year, month + 1, 0)),
		};
	}, [viewedMonthDate]);

	const goToMonth = useCallback(
		(target: Date) => setViewedMonth(target),
		[setViewedMonth],
	);
	const goToPreviousYear = useCallback(() => {
		setViewedMonth(
			new Date(
				viewedMonthDate.getFullYear() - 1,
				viewedMonthDate.getMonth(),
				1,
			),
		);
	}, [setViewedMonth, viewedMonthDate]);
	const goToNextYear = useCallback(() => {
		setViewedMonth(
			new Date(
				viewedMonthDate.getFullYear() + 1,
				viewedMonthDate.getMonth(),
				1,
			),
		);
	}, [setViewedMonth, viewedMonthDate]);

	// Match the month totals to the sidebar's selected status and other filters.
	const statsEntries = useMemo(() => {
		const predicate = makeEntryPredicate(effectiveFilters, dateFilter);
		return visibleEntries.filter(predicate);
	}, [visibleEntries, dateFilter, effectiveFilters]);

	const filterMenuSharedProps = {
		fieldOptions,
		filters: effectiveFilters,
		defaultFilters,
		onToggleFieldValue: toggleFilterFieldValue,
		onSelectAll: selectAllFilterValues,
		onSelectNone: selectNoFilterValues,
		onReset: resetFilters,
	};

	const hasActiveFilters = !isDefaultFilters(effectiveFilters, defaultFilters);

	return (
		<div className="w-full h-full flex flex-col">
			{/* Header — filter on the left, year timeline filling the rest. */}
			<div className="flex items-center gap-3 bg-background px-4 pt-4 pb-2">
				<FilterMenu
					{...filterMenuSharedProps}
					trigger={
						<Button
							variant="outline"
							size="icon"
							aria-label="Filter"
							className="relative h-11 w-11 shrink-0 self-center"
						>
							<Filter />
							{hasActiveFilters && (
								<span className="absolute right-1 top-1 size-1.5 rounded-full bg-primary" />
							)}
						</Button>
					}
				/>

				<Calendar2YearTimeline
					entries={visibleEntries}
					includeDeleted={statusValues.includes("deleted")}
					viewedMonth={viewedMonthDate}
					onSelectMonth={goToMonth}
					yearControls={
						<div className="flex h-11 shrink-0 items-center rounded-md border border-input bg-background">
							<Button
								variant="ghost"
								size="icon"
								aria-label="Previous year"
								className="h-full w-10 rounded-r-none"
								onClick={goToPreviousYear}
							>
								<ChevronLeft />
							</Button>
							<div className="flex h-full min-w-14 items-center justify-center border-x border-input px-2 text-center text-sm font-semibold tabular-nums text-foreground">
								{viewedMonthDate.getFullYear()}
							</div>
							<Button
								variant="ghost"
								size="icon"
								aria-label="Next year"
								className="h-full w-10 rounded-l-none"
								onClick={goToNextYear}
							>
								<ChevronRight />
							</Button>
						</div>
					}
				/>
			</div>

			{/* The month grid — fills the same rounded content shell EntriesView uses,
			    with StatsBar attached inside the container. */}
			<div className="flex-1 min-h-0 px-4 pb-4 pt-1">
				<div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-background">
					<Calendar2Grid
						month={viewedMonthDate}
						selectedDate={date}
						dateRange={dateRange}
						onDayClick={goToEntriesForDay}
						onWeekClick={goToEntriesForWeek}
						renderDay={(ctx) => (
							<DayHoursCell
								ctx={ctx}
								sums={sumsByDay.get(toYYYYMMDD(ctx.date))}
								statusValues={statusValues}
								billableValues={billableValues}
							/>
						)}
						renderWeek={(ctx) => (
							<WeekHoursCell
								ctx={ctx}
								sumsByDay={sumsByDay}
								statusValues={statusValues}
								billableValues={billableValues}
							/>
						)}
					/>
					<StatsBar
						entries={statsEntries}
						includeDeleted={statusValues.includes("deleted")}
					/>
				</div>
			</div>
		</div>
	);
}
